import Cookies from "universal-cookie";
import { authConfigExternal } from "../config/auth-config";
import User from "../models/user";

export type tokensObject = {
	access_token: string;
	refresh_token: string;
	id_token: string;
	expires_in: number;
	token_type: "Bearer";
	creation_date?: Date;
};

export type tokenError = {
	error: string;
};

export class AuthService {
	public authorizationCode?: string;
	private accessToken?: string;
	private refreshToken?: string;
	private idToken?: string;
	private expiry?: Date;
	public fetchingUser: boolean;

	constructor(cookies, authorizationCode = null) {
		if (authorizationCode) {
			this.authorizationCode = authorizationCode;
		}
		if (cookies.tokens) {
			let tokens = cookies.tokens as tokensObject;
			let expiresIn = tokens.expires_in;
			let creationDate = new Date(tokens.creation_date);
			let expiryDate = new Date(creationDate.getTime() + expiresIn * 60000);
			this.accessToken = tokens.access_token;
			this.refreshToken = tokens.refresh_token;
			this.idToken = tokens.id_token;
			this.expiry = expiryDate;
			this.fetchingUser = false;
		}
	}

	public async getTokensFromAuthCode(): Promise<string | tokensObject> {
		return new Promise((resolve, reject) => {
			this.fetchingUser = true;
			let formBody = [
				"grant_type=authorization_code",
				"client_id=" + authConfigExternal.clientId,
				"code=" + this.authorizationCode,
				"redirect_uri=" + authConfigExternal.redirectUri,
			];
			this.makeTokenRequest(formBody)
				.then((res) => {
					res = res as tokensObject;
					if (res.access_token) {
						res.creation_date = new Date();
						this.accessToken = res.access_token;
						this.refreshToken = res.refresh_token;
						this.idToken = res.id_token;
						this.expiry = new Date(
							res.creation_date.getTime() + res.expires_in * 60000
						);
						this.fetchingUser = false;
						resolve(res);
					} else {
						console.log("something has happened", res);
						this.fetchingUser = false;
						reject(res);
					}
				})
				.catch((err) => {
					this.fetchingUser = false;
					reject(err);
				});
		});
	}

	private async checkIfTokenIsExpired() {
		let today = new Date();
		if (this.expiry && today > this.expiry) {
			return await this.makeRefreshRequest();
		}
	}

	public async getUserInfo(): Promise<User | string> {
		return new Promise(async (resolve, reject) => {
			this.fetchingUser = true;
			this.checkIfTokenIsExpired()
				.then(() => {
					fetch(authConfigExternal.userEndpoint, {
						headers: {
							Authorization: "Bearer " + this.accessToken,
						},
					})
						.then((res) =>
							res.json().then((user) => {
								if (user.error) {
									reject(user.error);
								}
								let userObject: User = {
									email: user.email,
									cognitoUserId: user.username,
									userData: {
										firstName: "",
										lastName: "",
										uprn: "",
										telephone: "",
										alternateEmails: [],
										cxmId: "",
									},
								};
								resolve(userObject);
							})
						)
						.catch((err) => {
							if (err === "invalid_token") {
								this.makeRefreshRequest()
									.then((res) => {
										console.log("refresh result:", res);
										this.fetchingUser = false;
										resolve(this.getUserInfo());
									})
									.catch((err) => {
										this.fetchingUser = false;
										reject(err);
									});
							}
						});
				})
				.catch((err) => {
					reject(err);
				});
			if (!this.accessToken) {
				("This user has not been given an access token");
			}
		});
	}

	private async makeRefreshRequest(): Promise<string | void> {
		return new Promise((resolve, reject) => {
			let formBody = [
				"grant_type=refresh_token",
				"client_id=" + authConfigExternal.clientId,
				"refresh_token=" + this.refreshToken,
			];
			this.makeTokenRequest(formBody)
				.then((res) => {
					console.log(res);
					if ((res as tokensObject).access_token) {
						res = res as tokensObject;
						res.creation_date = new Date();
						this.accessToken = res.access_token;
						this.idToken = res.id_token;
						this.refreshToken = res.refresh_token;
						this.expiry = new Date(
							res.creation_date.getTime() + res.expires_in * 60000
						);
						resolve();
					} else {
						reject((res as tokenError).error);
					}
				})
				.catch((err) => {
					reject(err);
				});
		});
	}

	private async makeTokenRequest(
		formBody: string[]
	): Promise<tokensObject | tokenError> {
		return new Promise((resolve, reject) => {
			fetch(authConfigExternal.tokenEndpoint, {
				method: "POST",
				headers: {
					"Content-Type": "application/x-www-form-urlencoded",
				},
				body: formBody.join("&"),
			})
				.then((res) => resolve(res.json()))
				.catch((err) => reject(err));
		});
	}
}
